type AutoshipCadenceDefinition = {
    readonly frequency: string;
    readonly days: number;
    readonly label: string;
    readonly value: `${string}_${string}`;
} & (
    | {
          readonly unitOfMeasure: '2';
          readonly frequencyUnit: 'weeks';
      }
    | {
          readonly unitOfMeasure: '3';
          readonly frequencyUnit: 'months';
      }
);

export const AUTOSHIP_CADENCES = [
    {
        frequency: '2',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 14,
        value: '2_2',
        label: '2 weeks',
    },
    {
        frequency: '3',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 21,
        value: '3_2',
        label: '3 weeks',
    },
    {
        frequency: '4',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 28,
        value: '4_2',
        label: '4 weeks',
    },
    {
        frequency: '5',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 35,
        value: '5_2',
        label: '5 weeks',
    },
    {
        frequency: '6',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 42,
        value: '6_2',
        label: '6 weeks',
    },
    {
        frequency: '7',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 49,
        value: '7_2',
        label: '7 weeks',
    },
    {
        frequency: '8',
        unitOfMeasure: '2',
        frequencyUnit: 'weeks',
        days: 56,
        value: '8_2',
        label: '8 weeks',
    },
    {
        frequency: '3',
        unitOfMeasure: '3',
        frequencyUnit: 'months',
        days: 90,
        value: '3_3',
        label: '3 months',
    },
    {
        frequency: '4',
        unitOfMeasure: '3',
        frequencyUnit: 'months',
        days: 120,
        value: '4_3',
        label: '4 months',
    },
    {
        frequency: '6',
        unitOfMeasure: '3',
        frequencyUnit: 'months',
        days: 180,
        value: '6_3',
        label: '6 months',
    },
    {
        frequency: '9',
        unitOfMeasure: '3',
        frequencyUnit: 'months',
        days: 270,
        value: '9_3',
        label: '9 months',
    },
] as const satisfies readonly AutoshipCadenceDefinition[];

export type AutoshipCadence = (typeof AUTOSHIP_CADENCES)[number];
export type AutoshipFrequency = AutoshipCadence['frequency'];
export type AutoshipUnitOfMeasure = AutoshipCadence['unitOfMeasure'];
export type AutoshipCadenceValue = AutoshipCadence['value'];

export const AUTOSHIP_UNIT_OF_MEASURE_BY_FREQUENCY_UNIT = {
    weeks: '2',
    months: '3',
} as const satisfies Record<AutoshipCadence['frequencyUnit'], AutoshipUnitOfMeasure>;

const toUniqueValues = <Value extends string>(values: readonly Value[]): readonly Value[] => [...new Set(values)];

export const AUTOSHIP_FREQUENCY_VALUES = toUniqueValues(AUTOSHIP_CADENCES.map(({ frequency }) => frequency));
export const AUTOSHIP_UNIT_OF_MEASURE_VALUES = toUniqueValues(
    AUTOSHIP_CADENCES.map(({ unitOfMeasure }) => unitOfMeasure)
);

export const AUTOSHIP_ONE_TIME_OPTION = {
    label: 'One-time',
    value: 'one_time',
} as const;

/**
 * Dropdown selections encode a cadence as `frequency_unitOfMeasure`, so one-time repeats its own value.
 */
export const AUTOSHIP_ONE_TIME_SELECTION_VALUE =
    `${AUTOSHIP_ONE_TIME_OPTION.value}_${AUTOSHIP_ONE_TIME_OPTION.value}` as const;

export const DEFAULT_AUTOSHIP_CADENCE_VALUE = '4_2' satisfies AutoshipCadenceValue;

export const FIVE_AND_SEVEN_WEEK_AUTOSHIP_CADENCE_VALUES = [
    '5_2',
    '7_2',
] as const satisfies readonly AutoshipCadenceValue[];

export const isFiveOrSevenWeekAutoshipCadence = (value: string): boolean =>
    (FIVE_AND_SEVEN_WEEK_AUTOSHIP_CADENCE_VALUES as readonly string[]).includes(value);

export const getVisibleAutoshipCadences = (as_freq_5_7 = false): readonly AutoshipCadence[] =>
    as_freq_5_7
        ? AUTOSHIP_CADENCES
        : AUTOSHIP_CADENCES.filter(cadence => !isFiveOrSevenWeekAutoshipCadence(cadence.value));

export const getAutoshipFrequencyOptions = (as_freq_5_7 = false) =>
    getVisibleAutoshipCadences(as_freq_5_7).map(({ label, value }) => ({ label, value }));

export const getAutoshipCadenceByValue = (value: string): AutoshipCadence | undefined =>
    AUTOSHIP_CADENCES.find(cadence => cadence.value === value);

const toCadencePart = (value: string | number | null | undefined): string | undefined => {
    if (value == null || value === '') {
        return undefined;
    }

    return String(value);
};

/**
 * A SKU recommendation is valid only when both fields are present and the pair
 * maps to a visible Autoship selector cadence. 5- and 7-week pairs require `as_freq_5_7`.
 */
export const getRecommendedAutoshipCadence = (
    frequency?: string | number | null,
    unitOfMeasure?: string | number | null,
    as_freq_5_7 = false
): AutoshipCadence | undefined => {
    const mappedFrequency = toCadencePart(frequency);
    const mappedUnitOfMeasure = toCadencePart(unitOfMeasure);

    if (!mappedFrequency || !mappedUnitOfMeasure) {
        return undefined;
    }

    return getVisibleAutoshipCadences(as_freq_5_7).find(
        cadence => cadence.value === `${mappedFrequency}_${mappedUnitOfMeasure}`
    );
};

export type ResolveInitialAutoshipCadenceValueInput = {
    itemFrequency?: string | number | null;
    itemUnitOfMeasure?: string | number | null;
    recommendedFrequency?: string | number | null;
    recommendedUnitOfMeasure?: string | number | null;
    isEnabled: boolean;
    as_freq_5_7?: boolean;
};

/**
 * Initial cadence for a SKU: persisted item selection, then a valid recommendation
 * when the feature is on, otherwise the existing four-week default.
 * 5- and 7-week values are applied only when `as_freq_5_7` is true.
 */
export const resolveInitialAutoshipCadenceValue = ({
    itemFrequency,
    itemUnitOfMeasure,
    recommendedFrequency,
    recommendedUnitOfMeasure,
    isEnabled,
    as_freq_5_7 = false,
}: ResolveInitialAutoshipCadenceValueInput): AutoshipCadenceValue => {
    const itemFrequencyPart = toCadencePart(itemFrequency);
    const itemUnitOfMeasurePart = toCadencePart(itemUnitOfMeasure);

    if (itemFrequencyPart && itemUnitOfMeasurePart) {
        return (
            getRecommendedAutoshipCadence(itemFrequencyPart, itemUnitOfMeasurePart, as_freq_5_7)?.value ??
            DEFAULT_AUTOSHIP_CADENCE_VALUE
        );
    }

    if (isEnabled) {
        const recommendedCadence = getRecommendedAutoshipCadence(
            recommendedFrequency,
            recommendedUnitOfMeasure,
            as_freq_5_7
        );

        if (recommendedCadence) {
            return recommendedCadence.value;
        }
    }

    return DEFAULT_AUTOSHIP_CADENCE_VALUE;
};
